import React from "react";
import {
  Grid,
  Typography,
  FormControl,
  FormHelperText,
  FormLabel,
  Select,
  MenuItem,
  TextField,
  Box,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import CommonModal from "../../../component/model/ModelComponent";
import CommonButton from "../../../component/button/Button";
import InputComponent from "../../../component/input/Input";
import { formatAmount } from "../../../helper/formatAmount";
import dayjs from "dayjs";

const UmsatzplanungModal = ({
  isModalOpen,
  setIsModalOpen,
  formData,
  setFormData,
  errors,
  setErrors,
  handleSubmit,
  handleChange,
  handleDateChange,
  options,
  forderungenOptions,
  shouldDisableDate,
  selectedWeek,
  rechnungsartOptions,
  t,
  debouncedNettoBetrag,
  setDebouncedNettoBetrag,
  setTimer,
  setEditId,
}) => {
  const handleForderungSelect = (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        kunde: newValue.value || "",
        kundeDisplay: newValue.kunde || "",
        rechnungsNummer: newValue.rechnungsNummer || "",
        datum: newValue.datum ? dayjs(newValue.datum) : null,
        nettoBetrag: formatAmount(newValue.nettoBetrag) || "",
        umsatzsteuer: newValue.umsatzsteuer || "",
        steuer: newValue.steuer || "",
        bruttoBetrag: newValue.bruttoBetrag || "",
        rechnungsart: newValue.rechnungsart || "Rechnung",
        bemerkung: newValue.bemerkung || " ",
        ausVorwoche: newValue.ausVorwoche || "Nein",
      });
      if (setDebouncedNettoBetrag)
        setDebouncedNettoBetrag(newValue.nettoBetrag);
    }
  };

  const resetLocalForm = () => {
    setFormData({
      kunde: "",
      kundeDisplay: "",
      rechnungsNummer: null,
      datum: null,
      nettoBetrag: "",
      umsatzsteuer: "",
      steuer: "",
      bruttoBetrag: "",
      rechnungsart: "",
      bemerkung: "",
      ausVorwoche: "",
    });
    setDebouncedNettoBetrag("");
    setTimer(null);
    setEditId(null);
    setErrors({});
  };

  return (
    <CommonModal
      open={isModalOpen}
      onClose={() => {
        setIsModalOpen(false);
        resetLocalForm();
      }}
      title={t("umsatzplanung_umsatzplanung")}
      className="model-title"
      width={600}
    >
      <Typography
        component="div"
        sx={{
          border: "1px solid #E3E3E3",
          borderRadius: "10px",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh", // Prevents modal from going off-screen
          overflow: "hidden",
        }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {/* Scrollable Content Area */}
          <Box
            sx={{
              padding: "20px",
              overflowY: "auto",
              flex: 1,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: "10px",
              },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel
                    className="label"
                    sx={{ pb: "10px", display: "inline-block", color: "#000" }}
                  >
                    {t("umsatzplanung_forderung_waehlen")}
                  </FormLabel>
                  <Autocomplete
                    options={forderungenOptions || []}
                    getOptionLabel={(option) => `${option.label}`}
                    onChange={handleForderungSelect}
                    popupIcon={
                      <ExpandMoreIcon
                        sx={{ fontSize: "30px", color: "#33333399" }}
                      />
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        border: "1px solid rgba(227, 227, 227, 0.57)",
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t("umsatzplanung_suche_forderung")}
                        sx={{
                          "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                            padding: 0,
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
                <Box
                  sx={{
                    height: "1px",
                    bgcolor: "#E3E3E3",
                    my: 3,
                    width: "100%",
                  }}
                />
              </Grid>

              {/* Input Fields */}
              <Grid item xs={12} lg={6}>
                <FormControl fullWidth error={!!errors?.kundeDisplay}>
                  <InputComponent
                    type={"text"}
                    borderColor="E3E3E3"
                    border="2px solid #E3E3E3"
                    padding="10px 12px"
                    placeholder={t("forderungen_kunde")}
                    label={t("forderungen_kunde")}
                    className="input-field"
                    name={"kundeDisplay"}
                    value={formData?.kundeDisplay ?? ""}
                    onChange={handleChange}
                    required
                    disabled
                    error={!!errors.kundeDisplay}
                    helperText={errors.kundeDisplay}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} lg={6}>
                <InputComponent
                  type={"text"}
                  borderColor="E3E3E3"
                  border="2px solid #E3E3E3"
                  padding="10px 12px"
                  placeholder={"0"}
                  label={t("forderungen_rechnungsNummer")}
                  className="input-field"
                  name={"rechnungsNummer"}
                  value={formData?.rechnungsNummer || ""}
                  onChange={handleChange}
                  required
                  disabled
                  error={!!errors.rechnungsNummer}
                  helperText={errors.rechnungsNummer}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="de"
                >
                  <FormLabel
                    className="label"
                    sx={{
                      paddingTop: "1.25rem",
                      paddingBottom: "10px",
                      display: "inline-block",
                      color: "#000",
                    }}
                  >
                    {t("umsatzplanung_datum")}
                  </FormLabel>
                  <DatePicker
                    value={formData.datum}
                    onChange={(date) => handleDateChange("datum", date)}
                    shouldDisableDate={shouldDisableDate}
                    referenceDate={selectedWeek?.weekStart}
                    format="DD.MM.YYYY"
                    disabled
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#826320",
                        },
                      },
                    }}
                    slotProps={{
                      textField: {
                        error: !!errors.datum,
                        helperText: errors.datum || "",
                        fullWidth: true,
                        InputProps: {
                          sx: {
                            borderRadius: "10px",
                            border: "2px solid #E3E3E3",
                            "& .MuiInputBase-input": {
                              padding: "10px 12px",
                            },
                            "& input::placeholder": {
                              color: "#000",
                              fontSize: "14px",
                              opacity: 1,
                              fontFamily: "Poppins",
                            },
                          },
                        },
                        FormHelperTextProps: {
                          sx: {
                            color: !!errors.datum ? "#d32f2f" : "#14A9E2",
                            margin: "5px 0 0 0",
                            fontSize: "14px",
                            fontFamily: "Poppins",
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} lg={6}>
                <InputComponent
                  type={"text"}
                  borderColor="E3E3E3"
                  border="2px solid #E3E3E3"
                  padding="10px 12px"
                  placeholder={"100.000,00 EUR"}
                  label={t("forderungen_nettoBetrag")}
                  className="input-field"
                  name={"nettoBetrag"}
                  value={formData?.nettoBetrag ?? ""}
                  onChange={handleChange}
                  required
                  disabled
                  error={!!errors.nettoBetrag}
                  helperText={errors.nettoBetrag}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <FormControl fullWidth error={!!errors?.umsatzsteuer} disabled>
                  <FormLabel
                    className="label"
                    sx={{
                      paddingTop: "1.25rem",
                      paddingBottom: "10px",
                      display: "inline-block",
                      color: "#000",
                    }}
                  >
                    {t("forderungen_umsatzsteuer")}
                  </FormLabel>
                  <Select
                    className="select-input"
                    fullWidth
                    value={formData?.umsatzsteuer || ""}
                    onChange={(event) => {
                      handleChange(event);
                      setFormData({
                        ...formData,
                        umsatzsteuer: event.target.value,
                      });
                    }}
                    displayEmpty
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#826320 !important",
                        },
                      },
                    }}
                    inputProps={{
                      name: "umsatzsteuer",
                      IconComponent: () => (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingRight: "10px",
                          }}
                        >
                          <ExpandMoreIcon
                            sx={{ fontSize: "30px", color: "#33333399" }}
                          />
                        </span>
                      ),
                    }}
                    style={{
                      borderRadius: "10px",
                      border: "1px solid #e3e3e391",
                    }}
                    renderValue={(selected) =>
                      !selected ? (
                        <span style={{ color: "#999" }}>7%</span>
                      ) : (
                        `${selected}%`
                      )
                    }
                  >
                    {[
                      { value: "0", label: "0%" },
                      { value: "7", label: "7%" },
                      { value: "19", label: "19%" },
                    ].map((option, i) => (
                      <MenuItem key={i} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>

                  {errors?.umsatzsteuer && (
                    <FormHelperText
                      sx={{
                        margin: "0",
                        marginTop: "8px",
                        color: "#d32f2f",
                        fontSize: "14px",
                        fontFamily: "Poppins",
                      }}
                    >
                      {errors?.umsatzsteuer}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} lg={6}>
                <InputComponent
                  type={"text"}
                  borderColor="E3E3E3"
                  border="2px solid #E3E3E3"
                  padding="10px 12px"
                  placeholder={"100.000,00 EUR"}
                  label={t("forderungen_steuer")}
                  disabled
                  className="input-field"
                  name={"steuer"}
                  value={formData?.steuer ? formatAmount(formData?.steuer) : ""}
                  onChange={handleChange}
                  required
                  error={!!errors?.steuer}
                  helperText={errors?.steuer}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <InputComponent
                  type={"text"}
                  borderColor="E3E3E3"
                  border="2px solid #E3E3E3"
                  padding="10px 12px"
                  placeholder={"100.000,00 EUR"}
                  label={t("forderungen_bruttoBetrag")}
                  name={"bruttoBetrag"}
                  value={
                    formData?.bruttoBetrag
                      ? formatAmount(formData?.bruttoBetrag)
                      : ""
                  }
                  onChange={handleChange}
                  disabled
                  required
                  error={!!errors?.bruttoBetrag}
                  helperText={errors?.bruttoBetrag}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <FormControl fullWidth error={!!errors?.ausVorwoche}>
                  <FormLabel
                    className="label"
                    sx={{
                      paddingTop: "1.25rem",
                      paddingBottom: "10px",
                      display: "inline-block",
                      color: "#000",
                    }}
                  >
                    {t("umsatzplanung_aus_vorwoche")}
                  </FormLabel>
                  <Select
                    className="select-input"
                    fullWidth
                    value={formData?.ausVorwoche || ""}
                    onChange={(event) => {
                      handleChange(event);
                      setFormData({
                        ...formData,
                        ausVorwoche: event.target.value,
                      });
                    }}
                    displayEmpty
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#826320 !important",
                        },
                      },
                    }}
                    inputProps={{
                      name: "ausVorwoche",
                      IconComponent: () => (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingRight: "10px",
                          }}
                        >
                          <ExpandMoreIcon
                            sx={{ fontSize: "30px", color: "#33333399" }}
                          />
                        </span>
                      ),
                    }}
                    style={{
                      padding: "0",
                      borderRadius: "10px",
                      border: "1px solid #e3e3e391",
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: "#999" }}>
                            {t("common_ja")}
                          </span>
                        );
                      }
                      return t(`common_${selected.toLowerCase()}`);
                    }}
                  >
                    {[
                      { value: "Ja", label: t("common_ja") },
                      { value: "Nein", label: t("common_nein") },
                    ].map((option, i) => (
                      <MenuItem key={i} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} lg={6}>
                <FormControl fullWidth error={!!errors?.rechnungsart}>
                  <FormLabel
                    className="label"
                    sx={{
                      paddingTop: "1.25rem",
                      paddingBottom: "10px",
                      display: "inline-block",
                      color: "#000",
                    }}
                  >
                    {t("umsatzplanung_rechnungsart")}
                  </FormLabel>
                  <Select
                    className="select-input"
                    fullWidth
                    value={formData?.rechnungsart || ""}
                    onChange={(event) => {
                      handleChange(event);
                      setFormData({
                        ...formData,
                        rechnungsart: event.target.value,
                      });
                    }}
                    displayEmpty
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#826320 !important",
                        },
                      },
                    }}
                    inputProps={{
                      name: "rechnungsart",
                      IconComponent: () => (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingRight: "10px",
                          }}
                        >
                          <ExpandMoreIcon
                            sx={{ fontSize: "30px", color: "#33333399" }}
                          />
                        </span>
                      ),
                    }}
                    style={{
                      padding: "0",
                      borderRadius: "10px",
                      border: "1px solid #e3e3e391",
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: "#999" }}>
                            {t("umsatzplanung_rechnung")}
                          </span>
                        );
                      }
                      return t(`umsatzplanung_${selected.toLowerCase()}`);
                    }}
                  >
                    {rechnungsartOptions?.map((option, i) => (
                      <MenuItem key={i} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} lg={6}>
                <InputComponent
                  type={"text"}
                  borderColor="E3E3E3"
                  border="2px solid #E3E3E3"
                  padding="10px 12px"
                  placeholder={t("umsatzplanung_bemerkung")}
                  label={t("umsatzplanung_bemerkung")}
                  className="input-field"
                  name={"bemerkung"}
                  value={formData?.bemerkung ?? ""}
                  onChange={handleChange}
                  error={!!errors.bemerkung}
                  helperText={errors.bemerkung}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Sticky Action Footer */}
          <Box
            sx={{
              p: "20px",
              borderTop: "1px solid #E3E3E3",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              backgroundColor: "#fff",
              borderBottomLeftRadius: "10px",
              borderBottomRightRadius: "10px",
            }}
          >
            <CommonButton
              text={t("forderungen_zurücksetzen")}
              className="invitation-btn button-border-radius"
              bgColor="linear-gradient(90deg, #E14A4A 0%, #FF4848 100%);"
              onClick={resetLocalForm}
            />
            <CommonButton
              type="submit"
              text={`${t("forderungen_speichern")}`}
              className="add-user-btn button-border-radius"
            />
          </Box>
        </form>
      </Typography>
    </CommonModal>
  );
};

export default UmsatzplanungModal;
